import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = logged out

  const fetchMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("sl_user", JSON.stringify(data));
      return data;
    } catch {
      setUser(null);
      return null;
    }
  };

  useEffect(() => {
    // Try to re-establish session on every page load — the backend may use a
    // cookie (auto-sent) or a bearer token we've stored.
    const token = localStorage.getItem("sl_token");
    const cached = localStorage.getItem("sl_user");
    if (!token && !cached) {
      // still try, in case cookie is present
      fetchMe();
      return;
    }
    fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, pin) => {
    const { data } = await api.post("/auth/login", { username, pin });

    // Backend may return {token, user} OR just user OR empty (cookie-only)
    if (data && typeof data === "object") {
      const token =
        data.token || data.access_token || data.jwt || data.session_token;
      if (token) localStorage.setItem("sl_token", token);
      if (data.user) {
        setUser(data.user);
        localStorage.setItem("sl_user", JSON.stringify(data.user));
        return data.user;
      }
    }
    // Fall back: ask /auth/me for the current user
    const me = await fetchMe();
    if (!me) throw new Error("Login succeeded but /auth/me returned no user.");
    return me;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout").catch(() => {});
    } finally {
      localStorage.removeItem("sl_token");
      localStorage.removeItem("sl_user");
      localStorage.removeItem("sl_active_site");
      setUser(null);
    }
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, refresh: fetchMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
